// This is ScreenerFilter.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './ScreenerFilter.css';

import { METRIC_FIELDS } from "../constants/metricFields";


// const ALL_METRICS = [
//   { label: "Current Ratio", key: "currentRatio" },
//   { label: "Quick Ratio", key: "quickRatio" },
//   { label: "Gross Profit Margin", key: "grossProfitMargin" },
//   { label: "Operating Profit Margin", key: "operatingProfitMargin" },
//   { label: "Net Profit Margin", key: "netProfitMargin" },
//   { label: "Return on Assets", key: "returnOnAssets" },
//   { label: "Return on Equity", key: "returnOnEquity" },
//   { label: "Debt to Equity Ratio", key: "debtEquityRatio" },
//   { label: "Free Cash Flow per Share", key: "freeCashFlowPerShare" },
//   { label: "FCF / OCF Ratio", key: "freeCashFlowOperatingCashFlowRatio" },
//   { label: "Price to Free Cash Flow", key: "priceToFreeCashFlowsRatio" },
//   { label: "Price to Earnings Ratio", key: "priceEarningsRatio" },
//   { label: "Price to Sales Ratio", key: "priceToSalesRatio" }
// ];

const ALL_METRICS = METRIC_FIELDS
  .map(m => ({
    label: m.label,
    key: m.value
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const ScreenerFilter = ({ onFilterChange, activeFilters, metricsData }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [filters, setFilters] = useState({});

  // Calculate min/max ranges for all metrics from metricsData
  const metricRanges = useMemo(() => {
    if (!metricsData || Object.keys(metricsData).length === 0) return {};
    
    const ranges = {};
    
    ALL_METRICS.forEach(metric => {
      const values = Object.values(metricsData)
        // .map(stock => stock[metric.key])
                .filter(stock => stock !== null && stock !== undefined)
        .map(stock => stock[metric.key])
        .filter(val => val !== null && val !== undefined && !isNaN(val));
      
      if (values.length > 0) {
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.05; // 5% padding
        
        ranges[metric.key] = {
          min: Math.floor((min - padding) * 100) / 100,
          max: Math.ceil((max + padding) * 100) / 100,
          step: (max - min) > 100 ? 1 : 0.01
        };
      }
    });
    
    return ranges;
  }, [metricsData]);

  // Initialize filters when ranges are calculated
  useEffect(() => {
    if (Object.keys(metricRanges).length > 0) {
      const initialFilters = {};
      
      // Initialize from activeFilters if they exist, otherwise use full ranges
      ALL_METRICS.forEach(metric => {
        if (metricRanges[metric.key]) {
          const existing = activeFilters?.find(f => f.metric === metric.key);
          initialFilters[metric.key] = {
            min: existing?.min ?? metricRanges[metric.key].min,
            max: existing?.max ?? metricRanges[metric.key].max,
            enabled: !!existing
          };
        }
      });
      
      setFilters(initialFilters);
    }
  }, [metricRanges, activeFilters]);

  // Update filters when slider changes
  const handleSliderChange = useCallback((metricKey, type, value) => {
    setFilters(prev => {
      const updated = {
        ...prev,
        [metricKey]: {
          ...prev[metricKey],
          [type]: parseFloat(value),
          enabled: true
        }
      };
      
      // Convert to activeFilters format and call onFilterChange
      const activeFilters = Object.entries(updated)
        .filter(([_, filter]) => filter.enabled)
        .map(([key, filter]) => ({
          metric: key,
          min: filter.min,
          max: filter.max
        }));
      
      onFilterChange?.(activeFilters);
      return updated;
    });
  }, [onFilterChange]);

  // Calculate slider fill percentage for dynamic range visualization
  const getSliderFillStyle = useCallback((metricKey) => {
    const range = metricRanges[metricKey];
    const filter = filters[metricKey];
    
    if (!range || !filter) return { display: 'none' };
    
    const totalRange = range.max - range.min;
    const minPercent = ((filter.min - range.min) / totalRange) * 100;
    const maxPercent = ((filter.max - range.min) / totalRange) * 100;
    
    return {
      left: `${minPercent}%`,
      width: `${maxPercent - minPercent}%`
    };
  }, [metricRanges, filters]);

  // Toggle metric filter on/off
  const toggleMetricFilter = useCallback((metricKey) => {
    setFilters(prev => {
      const updated = {
        ...prev,
        [metricKey]: {
          ...prev[metricKey],
          enabled: !prev[metricKey]?.enabled
        }
      };
      
      // Convert to activeFilters format and call onFilterChange
      const activeFilters = Object.entries(updated)
        .filter(([_, filter]) => filter.enabled)
        .map(([key, filter]) => ({
          metric: key,
          min: filter.min,
          max: filter.max
        }));
      
      onFilterChange?.(activeFilters);
      return updated;
    });
  }, [onFilterChange]);

  // Reset all filters
  const resetAllFilters = useCallback(() => {
    const resetFilters = {};
    ALL_METRICS.forEach(metric => {
      if (metricRanges[metric.key]) {
        resetFilters[metric.key] = {
          min: metricRanges[metric.key].min,
          max: metricRanges[metric.key].max,
          enabled: false
        };
      }
    });
    
    setFilters(resetFilters);
    onFilterChange?.([]);
  }, [metricRanges, onFilterChange]);

  const activeFilterCount = Object.values(filters).filter(f => f.enabled).length;

  return (
    <div className="screener-filter-container">
      <div className="screener-filter-header">
        <h3>Filters</h3>
        <div className="filter-header-actions">
          {activeFilterCount > 0 && (
            <span className="active-filter-count">{activeFilterCount}</span>
          )}
          <button 
            className="collapse-btn" 
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? "Expand filters" : "Collapse filters"}
          >
            {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
          </button>
        </div>
      </div>

      <div className={`screener-filter-content ${isCollapsed ? 'collapsed' : ''}`}>
        {activeFilterCount > 0 && (
          <button className="reset-filters-btn" onClick={resetAllFilters}>
            Clear all
          </button>
        )}
        
        <div className="metrics-list">
          {ALL_METRICS.map((metric) => {
            const range = metricRanges[metric.key];
            const filter = filters[metric.key];
            
            if (!range || !filter) return null;

            return (
              <div key={metric.key} className="metric-filter-item">
                <div className="metric-filter-header">
                  <label className="metric-checkbox">
                    <input
                      type="checkbox"
                      checked={filter.enabled || false}
                      onChange={() => toggleMetricFilter(metric.key)}
                    />
                    <span className="metric-label">{metric.label}</span>
                  </label>
                </div>
                
                {filter.enabled && (
                  <div className="slider-container">
                    <div className="slider-values">
                      <span>{filter.min}</span>
                      <span>{filter.max}</span>
                    </div>
                    
                    <div className="dual-range-slider">
                      <div 
                        className="slider-fill" 
                        style={getSliderFillStyle(metric.key)}
                      />
                      <input
                        type="range"
                        min={range.min}
                        max={range.max}
                        step={range.step}
                        value={filter.min}
                        onChange={(e) => handleSliderChange(metric.key, 'min', e.target.value)}
                        className="range-slider range-slider-min"
                      />
                      <input
                        type="range"
                        min={range.min}
                        max={range.max}
                        step={range.step}
                        value={filter.max}
                        onChange={(e) => handleSliderChange(metric.key, 'max', e.target.value)}
                        className="range-slider range-slider-max"
                      />
                    </div>
                    
                    <div className="range-labels">
                      <span className="range-min">{range.min}</span>
                      <span className="range-max">{range.max}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ScreenerFilter;
